/**
 * TopBarSlotContext — lets a routed page project context-specific content
 * (e.g. the current dashboard's title + actions + filters) into the
 * persistent app top bar's otherwise-empty left region.
 *
 * Split into TWO contexts on purpose: the SETTER is stable (a useState
 * setter), so a page that only projects content (``useTopBarContent``) never
 * re-renders when the content changes — only the reader (the top bar /
 * ``useTopBarSlotContent``) does. A single combined context would re-render
 * every projecting page on each update, adding wasteful cycles.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

const ContentContext = createContext<React.ReactNode>(null);
type SetContent = (node: React.ReactNode) => void;
const SetContentContext = createContext<SetContent>(() => {});

export const TopBarSlotProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [content, setContent] = useState<React.ReactNode>(null);
  return (
    <SetContentContext.Provider value={setContent}>
      <ContentContext.Provider value={content}>
        {children}
      </ContentContext.Provider>
    </SetContentContext.Provider>
  );
};

/** Read the projected slot content — used by the top bar to render it. */
export const useTopBarSlotContent = (): React.ReactNode =>
  useContext(ContentContext);

/**
 * Project ``node`` into the top bar for as long as the calling component is
 * mounted. Recomputes when ``deps`` change; clears on unmount so other pages
 * start blank. Subscribes only to the stable setter, so updating the content
 * never re-renders the projecting page. Callers memoise ``node`` via ``deps``
 * exactly as they would a ``useMemo``.
 */
export const useTopBarContent = (
  node: React.ReactNode,
  deps: React.DependencyList,
): void => {
  const setContent = useContext(SetContentContext);
  useEffect(() => {
    setContent(node);
    return () => setContent(null);
    // node is intentionally recomputed from deps by the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setContent, ...deps]);
};
